import { addDays, format, isValid, parse, parseISO } from 'date-fns';
import { formatInTimeZone, toDate, toZonedTime } from 'date-fns-tz';
import {
  DEFAULT_SCHEDULE_TIME_ZONE,
  formatIsoInScheduleZone,
  resolveScheduleTimeZone,
  scheduleZoneDisplaySuffix,
} from '@/lib/schedule-timezone';

export type AdminBusinessEtStacked = {
  dateLine: string;
  timeLine: string;
};

function parseAdminDateInput(value: Date | string | number | undefined | null): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  if (!s) return null;
  const iso = parseISO(s);
  if (isValid(iso)) return iso;
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Program start date from checkout (`Order.startDate`; `startMonth` when no day). */
const ENROLLMENT_START_DATE_FORMATS = ['MM/dd/yyyy', 'M/d/yyyy', 'yyyy-MM-dd'] as const;

function parseEnrollmentStartDate(value: string): Date | null {
  const s = value.trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s) || s.includes('T')) {
    const iso = parseISO(s);
    if (isValid(iso)) return iso;
  }
  for (const fmt of ENROLLMENT_START_DATE_FORMATS) {
    const d = parse(s, fmt, new Date());
    if (isValid(d)) return d;
  }
  const fallback = new Date(s);
  return isValid(fallback) ? fallback : null;
}

/** Calendar date `YYYY-MM-DD` in US Eastern (matches order `startDate` + start-date email cron). */
export function getEasternCalendarDateIso(offsetDays = 0): string {
  const zonedNow = toZonedTime(new Date(), DEFAULT_SCHEDULE_TIME_ZONE);
  return format(addDays(zonedNow, offsetDays), 'yyyy-MM-dd');
}

/** Date-only display, e.g. `May 21, 2026`. */
export function formatEnrollmentStartDate(
  startDate?: string | null,
  startMonth?: string | null,
): string {
  if (startDate?.trim()) {
    const d = parseEnrollmentStartDate(startDate);
    if (d) return format(d, 'MMMM d, yyyy');
  }
  const month = startMonth?.trim();
  return month || '—';
}

/**
 * Policy “business” timestamps for admin (submissions, payments, etc.):
 * wall clock in {@link DEFAULT_SCHEDULE_TIME_ZONE} with controlled suffix (` EST`).
 */
export function formatAdminBusinessEt(iso: string | undefined | null): string {
  if (iso == null || iso === '') return '—';
  return formatIsoInScheduleZone(iso, DEFAULT_SCHEDULE_TIME_ZONE);
}

/** Business Eastern for `Date`, epoch ms, or ISO-like string from APIs. */
export function formatAdminBusinessEtInput(value: Date | string | number | undefined | null): string {
  if (value == null) return '—';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '—';
    return formatIsoInScheduleZone(value.toISOString(), DEFAULT_SCHEDULE_TIME_ZONE);
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return formatIsoInScheduleZone(d.toISOString(), DEFAULT_SCHEDULE_TIME_ZONE);
  }
  const s = String(value).trim();
  if (!s) return '—';
  return formatIsoInScheduleZone(s, DEFAULT_SCHEDULE_TIME_ZONE);
}

/** Date on first line, time + zone suffix on second (narrower table cells). */
export function getAdminBusinessEtStacked(
  value: Date | string | number | undefined | null
): AdminBusinessEtStacked | null {
  const d = parseAdminDateInput(value);
  if (!d) return null;
  const tz = DEFAULT_SCHEDULE_TIME_ZONE;
  try {
    return {
      dateLine: formatInTimeZone(d, tz, 'MMM d, yyyy'),
      timeLine: `${formatInTimeZone(d, tz, 'h:mm a')}${scheduleZoneDisplaySuffix(tz)}`,
    };
  } catch {
    return null;
  }
}

/** Audit / system sequence: UTC with explicit labeling. */
export function formatAdminAuditUtc(iso: string | undefined | null): string {
  if (iso == null || iso === '') return '—';
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    const core = formatInTimeZone(d, 'UTC', 'MMM d, yyyy h:mm a');
    return `${core} UTC`;
  } catch {
    return iso;
  }
}

export function formatAdminAuditUtcInput(value: Date | string | number | undefined | null): string {
  if (value == null) return '—';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '—';
    return formatAdminAuditUtc(value.toISOString());
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return formatAdminAuditUtc(d.toISOString());
  }
  const s = String(value).trim();
  if (!s) return '—';
  return formatAdminAuditUtc(s);
}

/**
 * `datetime-local` value (no offset) interpreted as wall time in `timeZone`, returned as UTC ISO for APIs.
 * Matches attendance filter contract (explicit instant, `Z` in JSON).
 */
export function zonedDatetimeLocalInputToUtcIso(input: string, timeZone: string): string | undefined {
  if (!input?.trim()) return undefined;
  const normalized = input.length === 16 ? `${input}:00` : input;
  const tz = resolveScheduleTimeZone(timeZone);
  try {
    const d = toDate(normalized, { timeZone: tz });
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString();
  } catch {
    return undefined;
  }
}

/** UTC ISO → `datetime-local` wall clock in `timeZone` (for controlled EST editors). */
export function utcIsoToZonedDatetimeLocalInput(
  iso: string | null | undefined,
  timeZone: string = DEFAULT_SCHEDULE_TIME_ZONE
): string {
  if (!iso?.trim()) return '';
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatInTimeZone(d, resolveScheduleTimeZone(timeZone), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

/** Current wall clock in `timeZone` as `datetime-local` value. */
export function nowZonedDatetimeLocalInput(
  timeZone: string = DEFAULT_SCHEDULE_TIME_ZONE
): string {
  return formatInTimeZone(new Date(), resolveScheduleTimeZone(timeZone), "yyyy-MM-dd'T'HH:mm");
}

/** Short label for filter UI (slot range interpreted in this zone). */
export function shortScheduleZoneFilterLabel(timeZone: string): string {
  const z = resolveScheduleTimeZone(timeZone);
  switch (z) {
    case 'America/New_York':
      return 'US Eastern (EST)';
    case 'America/Chicago':
      return 'US Central (CT)';
    case 'America/Denver':
      return 'US Mountain (MT)';
    case 'America/Los_Angeles':
      return 'US Pacific (PT)';
    default:
      return z;
  }
}
